/**
 * Checks if a given number is a non-negative number
 *
 * @param {number} value
 * @param canAlsoBeNaN by default, false
 * @returns true when it is a number greater than or equal to zero; false, otherwise. This also returns true when canAlsoBeNaN is true.
 */
export function isNonNegativeNumber(
    value: number,
    canAlsoBeNaN = false
): boolean {
    if (typeof value == "number") {
        if (canAlsoBeNaN) {
            return !(value < 0);
        } else {
            return value >= 0;
        }
    } else {
        return false;
    }
}

/**
 * Checks if a given number is a defined number and not NaN.
 *
 * @param {number} value
 * @returns true when it is a defined number; false, otherwise
 */
export function isNumberAndNotNaN(value: number | undefined) {
    return typeof value === "number" && !Number.isNaN(value);
}

/**
 * Calculates a value of scaling factor
 *
 * @param {number} value
 * @returns a number with up to 3 digits after the decimal point
 */
export function calculateScalingFactorValue(value: number) {
    return Number(Number(value / 100).toFixed(3));
}

/**
 * Calculates a value of group score weight
 *
 * @param {number} value
 * @returns a number with up to one decimal place
 */
export function calculateGroupWeight(value: number) {
    return Number(Number(value).toFixed(3)) * 100;
}

/**
 * Checks if a given number is a non-negative integer
 *
 * @param {number} value
 * @returns true if it is a non-negative integer; otherwise, false
 */
export function isNonNegativeInteger(value: number) {
    return Number.isInteger(value) && value >= 0;
}

/**
 * Checks if a given value is a positive integer less than or equal to a given max limit
 *
 * @param {number | undefined} value
 * @param {number} maxLimit
 * @returns true when a value is a positive integer <= maxLimit; false, otherwise
 */
export function isPositiveIntegerWithinMaxLimit(
    value: number | undefined,
    maxLimit: number
) {
    if (isNumberAndNotNaN(value) && value !== undefined) {
        return (value | 0) === value && value > 0 && value <= maxLimit;
    } else {
        return false;
    }
}
